import { birthdayStatusAtom, birthdayStatusViewAtom } from "@/atoms/birthday-prompt"
import { BirthdaySelects, type BirthdaySelection } from "@/components/birthday/BirthdaySelects"
import { showToast } from "@/components/feedback"
import { Button, Card, FieldError, Heading, SkeletonText, Text } from "@/components/ui"
import { saveBirthday } from "@/domain/birthday/api"
import { formatBirthday, parseBirthdayDate, type BirthdayDate } from "@/domain/birthday/calendar"
import { TRANSITION } from "@/lib/motion"
import { useAtomValue, useSetAtom } from "jotai"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useId, useState, type FormEvent } from "react"

/** "Aniversário" in Minha conta: add or change day and month at any time, even after "Não quero informar". */
export function BirthdayCard() {
  const status = useAtomValue(birthdayStatusViewAtom)
  const titleId = useId()
  const saved = status?.birthday ?? null

  return (
    <Card as="section" padding="lg" aria-labelledby={titleId} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Heading id={titleId} level={2} size="card">
          Aniversário
        </Heading>
        {!saved && (
          <Text size="sm" tone="muted">
            Conte o dia e o mês e a loja pode te mimar nessa data.
          </Text>
        )}
      </div>
      {status === undefined ? <SkeletonText lines={2} /> : <BirthdayEditor initial={saved} />}
    </Card>
  )
}

type BirthdayEditorProps = {
  initial: BirthdayDate | null
}

function BirthdayEditor({ initial }: BirthdayEditorProps) {
  const refreshStatus = useSetAtom(birthdayStatusAtom)
  const reduceMotion = useReducedMotion()
  const [baseline, setBaseline] = useState(initial)
  const [selection, setSelection] = useState<BirthdaySelection>({
    day: initial ? String(initial.day) : "",
    month: initial ? String(initial.month) : "",
  })
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const date = parseBirthdayDate(selection.day, selection.month)
  const changed = date !== null && (date.day !== baseline?.day || date.month !== baseline?.month)

  const expanded = baseline === null || editing

  function cancel() {
    setSelection({ day: baseline ? String(baseline.day) : "", month: baseline ? String(baseline.month) : "" })
    setFailed(false)
    setEditing(false)
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!date || !changed || saving) return
    setSaving(true)
    setFailed(false)
    try {
      await saveBirthday(date)
      setBaseline(date)
      setEditing(false)
      refreshStatus()
      showToast.success("Aniversário salvo!")
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  const collapse = reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }
  const open = reduceMotion ? { opacity: 1 } : { height: "auto", opacity: 1 }

  return (
    <div className="flex flex-col gap-3">
      <AnimatePresence initial={false}>
        {!expanded && baseline && (
          <motion.div
            key="summary"
            className="flex items-center justify-between gap-3"
            initial={collapse}
            animate={{ ...open, transition: TRANSITION.size }}
            exit={{ ...collapse, transition: TRANSITION.exit }}
          >
            <Text size="sm" tone="muted">{`Seu aniversário: ${formatBirthday(baseline)}`}</Text>
            <Button type="button" variant="text" size="md" onClick={() => setEditing(true)}>
              Alterar
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.form
            key="form"
            className="flex flex-col gap-3 overflow-hidden"
            initial={collapse}
            animate={{ ...open, transition: TRANSITION.size }}
            exit={{ ...collapse, transition: TRANSITION.exit }}
            onSubmit={(event) => void save(event)}
            noValidate
          >
            <BirthdaySelects
              day={selection.day}
              month={selection.month}
              disabled={saving}
              onChange={(next) => {
                setSelection(next)
                setFailed(false)
              }}
            />
            {failed && (
              <FieldError className="animate-fade-in motion-reduce:animate-none">Não deu pra salvar agora. Tente de novo.</FieldError>
            )}
            <AnimatePresence initial={false}>
              {changed && (
                <motion.div
                  key="save"
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0, transition: TRANSITION.size }}
                  exit={{ opacity: 0, transition: TRANSITION.exit }}
                >
                  <Button type="submit" size="md" fullWidth isLoading={saving} disabled={saving}>
                    {saving ? "Salvando..." : "Salvar aniversário"}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            {baseline && (
              <Button type="button" variant="text" size="md" onClick={cancel} disabled={saving}>
                Cancelar
              </Button>
            )}
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  )
}
